import { GetStaticProps } from 'next';
import { WobRequestOverviewSingleFieldsFragment, PageInformationFieldsFragment, WobRequest } from 'api/graphql-types';
import Layout from 'modules/layout/layout';
import { ModuleBuilder } from 'modules/module-builder/module-builder';
import client, { httpLink, setPreviewSecret } from 'api/apollo-client';
import { getServerPageGetWobRequestOverview } from 'api/graphql-helper';
import { Head } from 'modules/head/head';
import { initialState as settingsInitialState } from 'redux/stores/settings';
import { initialState as wobRequestsInitialState } from 'redux/stores/wob-requests';
import { getStrapiParametersFromContext } from 'utils/strapi-parameters';
import { ROUTES } from 'constants/routes';
import { IBasePageProps } from 'types/page';

interface IWobRequestOverviewPageProps extends IBasePageProps {
	wobRequestOverview?: WobRequestOverviewSingleFieldsFragment;
	pageInformation?: PageInformationFieldsFragment;
}

const WobRequestOverviewPage = ({
	pageInformation,
	wobRequestOverview
}: IWobRequestOverviewPageProps): React.JSX.Element => {
	const { header, bodyText, list, modules } = { ...wobRequestOverview };
	const seoModule = wobRequestOverview?.seo;

	let modulesList: any[] = [header, bodyText, list];

	if (modules && modules?.length) {
		modulesList = [...modulesList, ...modules];
	}

	return (
		<Layout navbar={pageInformation?.navbar} footer={pageInformation?.footer}>
			<Head seoModule={seoModule} seoParent={pageInformation?.seo} />
			<ModuleBuilder modules={modulesList} slug={ROUTES.WOB_REQUESTS.fullPath} />
		</Layout>
	);
};

export const getStaticProps: GetStaticProps<IWobRequestOverviewPageProps> = async (ctx) => {
	try {
		const { locale, publicationState, draftMode } = getStrapiParametersFromContext(ctx);

		if (publicationState === 'DRAFT') {
			client.setLink(setPreviewSecret(process.env.STRAPI_PREVIEW_SECRET as string).concat(httpLink));
		}

		const result = await getServerPageGetWobRequestOverview(
			{ variables: { locale, publicationState, limit: 10 } },
			client
		);
		const { pageInformation, wobRequestOverview, wobRequests_connection: wobRequests } = result.props.data;

		if (wobRequestOverview == null) {
			return {
				notFound: true
			};
		}

		return {
			props: {
				pageInformation: pageInformation?.data?.attributes,
				wobRequestOverview: wobRequestOverview.data?.attributes,
				initialReduxState: {
					settings: {
						...settingsInitialState,
						publicationState,
						draftMode,
						contentType: 'wob-request-overview',
						contentTypeId: wobRequestOverview?.data?.documentId
					},
					wobRequests: {
						...wobRequestsInitialState,
						wobRequestItems: wobRequests?.data?.map((item) => item.attributes as WobRequest),
						totalWobRequestItems: wobRequests?.pageInfo.total || 0
					}
				}
			},
			revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_TIME_IN_SECONDS)
		};
	} catch (error: any) {
		// eslint-disable-next-line no-console
		console.error('Error in wob request overview', error);

		return {
			notFound: true
		};
	}
};

export default WobRequestOverviewPage;
